import { BaseTextInput } from 'components/FormUtils/TextInput/text-input'
import { BaseExportModal } from 'components/Modals/ExportModal/ExportModal'
import { Modal } from 'components/UI/Modal/Modal'
import { Select } from 'components/UI/Select/Select'
import { Switch } from 'components/UI/Switch/Switch'
import { FBPage } from '../../components/FbUI/Page/FBPage'
import { ImporterProvider, useImporterViewModel } from './ImporterViewModel'
import { ImportWizard } from './Wizard/ImportWizard'
import { ManualSearchModal } from './ManualSearchModal'
import { ValidateModal } from './validate-modal'
import { FBPageTableContainer } from 'components/FbUI/Page/styles'
import { Spinner } from 'react-bootstrap'
import { Loader2 } from 'lucide-react'
import {
  useWizardMachine,
  WizardMachineReturn,
} from './Wizard/useWizardMachine'
import { createContext, useContext } from 'react'
import { getProcessMessage } from './Wizard/types'
import FbButton from 'components/FbUI/FbButton'

const TABLE_CONTAINER_HEIGHT = 'calc(var(--base-page-table-height))'

// Context to share wizard machine with child components
const WizardContext = createContext<WizardMachineReturn | null>(null)

export function useWizard(): WizardMachineReturn {
  const context = useContext(WizardContext)
  if (!context) {
    throw new Error('useWizard must be used within Importer')
  }
  return context
}

export default function Importer(props: { importNumber: number }) {
  // Wizard machine owns all data fetching and process polling
  const wizard = useWizardMachine(props.importNumber)

  // Show loading spinner during initial fetch
  if (wizard.isInitializing) {
    return (
      <div className="flex flex-col w-full h-full justify-center items-center">
        <div className="text-sm">
          <Spinner animation="border" size="sm" className="mr-2" />
        </div>
      </div>
    )
  }

  // Show processing state when waiting for backend process
  if (wizard.isWaitingForProcess || wizard.isProcessing) {
    return (
      <div className="flex flex-col w-full h-full justify-center items-center gap-4">
        <Loader2 className="h-12 w-12 text-gray-400 animate-spin" />
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-xl font-semibold text-gray-800">
            {getProcessMessage(wizard.currentProcess)}
          </h2>
          <p className="text-sm text-gray-500">
            This process is running in the background.
          </p>
          <p className="text-sm text-gray-500">
            You can wait here or come back later.
          </p>
        </div>
      </div>
    )
  }

  // Show error state with retry option
  if (wizard.isError) {
    return (
      <div className="flex flex-col w-full h-full justify-center items-center gap-4">
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-xl font-semibold text-red-600">
            An error occurred
          </h2>
          <p className="text-sm text-gray-500">{wizard.error}</p>
          <FbButton onClick={wizard.retry}>Retry</FbButton>
        </div>
      </div>
    )
  }

  // Data is ready - provide to ImporterProvider
  return (
    <WizardContext.Provider value={wizard}>
      <ImporterProvider
        importNumber={props.importNumber}
        initialImportData={wizard.importData}
        initialSkipMatching={wizard.skipMatching}
        dataVersion={wizard.dataVersion}
      >
        <ImporterContent />
      </ImporterProvider>
    </WizardContext.Provider>
  )
}

function ImporterContent() {
  const viewModel = useImporterViewModel()

  return (
    <FBPage>
      <FBPageTableContainer style={{ height: TABLE_CONTAINER_HEIGHT }}>
        <ImportWizard />
      </FBPageTableContainer>

      <ManualSearchModal
        show={viewModel.selectedRowId !== null}
        onClose={() => {
          viewModel.setSelectedRowId(null)
        }}
        onSelect={viewModel.handleManualSearchSelect}
      />

      <ValidateModal
        open={!!viewModel.validateModalData}
        onClose={() => viewModel.setValidateModalData(null)}
        onValidate={(data) => {
          viewModel.handleValidation(data.changes, data.columnName)
        }}
        data={viewModel.validateModalData}
      />

      {/* Select Deal Product Modal */}
      <Modal
        open={viewModel.showSelectDealProductModal}
        disableCloseIcon
        title="Select Deal Product"
        description="The deal import requires a product to be selected. Since the product column is not present in the uploaded CSV, you must select the product from the dropdown below."
        size="lg"
        blockAccept={viewModel.selectedDealProduct == null}
        acceptButtonText="Select Deal Product"
        onAccept={() => {
          if (viewModel.selectedDealProduct) {
            viewModel.handleSelectDealProduct(viewModel.selectedDealProduct)
          }
        }}
        onCancel={() => {
          viewModel.setShowSelectDealProductModal(false)
          viewModel.setSelectedDealProduct(null)
        }}
      >
        <Select
          className="w-full"
          items={viewModel.products?.map((product) => ({
            label: product.name,
            value: product.name,
          }))}
          selected={viewModel.selectedDealProduct}
          onSelect={(value) => viewModel.setSelectedDealProduct(value)}
        />
      </Modal>

      <Modal
        open={viewModel.showConfigureExportModal}
        title="Configure Export"
        description="Configure the export options for the importer."
        size="lg"
        onCancel={() => viewModel.setShowConfigureExportModal(false)}
        acceptButtonText="Done"
        onAccept={() => viewModel.handleConfigureExport()}
      >
        <div className="flex flex-col gap-6 p-2">
          {/* Suggestions Toggle */}
          <div className="flex flex-row items-center gap-3">
            <Switch
              checked={viewModel.exportConfig.allowSuggestions}
              onChange={(e) =>
                viewModel.setExportConfig({
                  ...viewModel.exportConfig,
                  allowSuggestions: e.target.checked,
                })
              }
            />
            <div className="font-medium text-gray-800">
              Include Suggested Data
            </div>
          </div>

          {/* Suggestion Amount */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="suggestionAmount"
              className={`text-sm font-medium text-gray-700 ${
                !viewModel.exportConfig.allowSuggestions ? 'opacity-50' : ''
              }`}
            >
              Suggestion Amount
            </label>
            <BaseTextInput
              value={viewModel.exportConfig.suggestionAmount}
              name="suggestionAmount"
              type="number"
              min={1}
              max={10}
              disabled={!viewModel.exportConfig.allowSuggestions}
              onChange={(value) =>
                viewModel.setExportConfig({
                  ...viewModel.exportConfig,
                  suggestionAmount: Number(value),
                })
              }
              className="w-32"
            />
            <span className="text-xs text-gray-500">
              Number of suggestions to include per row (1-10)
            </span>
          </div>
        </div>
      </Modal>

      <BaseExportModal
        title="Select Columns to Export"
        subTitle="Export up to 10,000 rows at a time, with any of the following columns. Any filters already applied will remain active."
        submitText="Export"
        columns={viewModel.exportColumns}
        onSubmit={viewModel.handleExport}
        show={viewModel.showExportModal}
        handleClose={() => viewModel.setShowExportModal(false)}
        disableExportTypes
      />
    </FBPage>
  )
}
